"use client";

import QosmCode from "./QosmCode";
import CodeAnimation from "./CodeAnimation";
import CyclingWord from "./CyclingWord";
import { WaitlistButton } from "./WaitlistForm";
import { accent } from "@/lib/palette";

const preludeCode = `extern read_file : {path: String} -> String ! {File.Read};
extern post_msg  : {channel: String, text: String} -> Unit ! {Slack.Write};`;

const capabilities = [
  {
    extern: "read_file",
    label: "File.Read",
    color: "#38bdf8",
    attenuations: [
      { key: "path", value: "/data/*" },
    ],
  },
  {
    extern: "post_msg",
    label: "Slack.Write",
    color: "#34d399",
    attenuations: [
      { key: "server", value: "acme-corp.slack.com" },
      { key: "auth", value: "xoxb-****-7f3a" },
      { key: "channel", value: "#analytics" },
    ],
  },
];

export default function HeroDark() {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-16 overflow-hidden max-md:min-h-0 max-md:pt-24 max-md:pb-12">
      {/* Starfield background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 animate-drift"
          style={{
            backgroundImage: [
              "radial-gradient(1px 1px at 10% 15%, rgba(220,215,245,0.4), transparent)",
              "radial-gradient(1px 1px at 25% 35%, rgba(220,215,245,0.25), transparent)",
              "radial-gradient(1px 1px at 40% 8%, rgba(220,215,245,0.3), transparent)",
              `radial-gradient(1.5px 1.5px at 55% 65%, ${accent.star}, transparent)`,
              "radial-gradient(1px 1px at 70% 20%, rgba(220,215,245,0.35), transparent)",
              "radial-gradient(1px 1px at 85% 45%, rgba(220,215,245,0.2), transparent)",
              "radial-gradient(1px 1px at 15% 70%, rgba(220,215,245,0.3), transparent)",
              "radial-gradient(1px 1px at 90% 80%, rgba(220,215,245,0.25), transparent)",
              "radial-gradient(1px 1px at 5% 90%, rgba(220,215,245,0.15), transparent)",
              `radial-gradient(1px 1px at 50% 50%, ${accent.star}, transparent)`,
              "radial-gradient(1px 1px at 35% 85%, rgba(220,215,245,0.2), transparent)",
              "radial-gradient(1px 1px at 60% 10%, rgba(220,215,245,0.3), transparent)",
              "radial-gradient(1px 1px at 78% 55%, rgba(220,215,245,0.2), transparent)",
              "radial-gradient(1px 1px at 20% 50%, rgba(220,215,245,0.25), transparent)",
              `radial-gradient(1.5px 1.5px at 45% 30%, ${accent.star}, transparent)`,
              "radial-gradient(1px 1px at 8% 40%, rgba(220,215,245,0.2), transparent)",
              "radial-gradient(1px 1px at 65% 75%, rgba(220,215,245,0.3), transparent)",
              "radial-gradient(1px 1px at 95% 15%, rgba(220,215,245,0.2), transparent)",
              "radial-gradient(1px 1px at 30% 95%, rgba(220,215,245,0.15), transparent)",
              "radial-gradient(1px 1px at 75% 40%, rgba(220,215,245,0.25), transparent)",
            ].join(", "),
          }}
        />
        {/* Accent glow behind headline */}
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] max-md:w-[400px] max-md:h-[250px]"
          style={{
            background: `radial-gradient(ellipse at center, ${accent.glow}, transparent 70%)`,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-[750px] mx-auto mb-16 max-md:mb-10">
        <h1
          className="text-[clamp(2.25rem,5.5vw,4rem)] font-bold leading-[1.12] tracking-tight text-text-primary mb-6 animate-fade-in-up max-md:mb-4"
        >
          Your <CyclingWord /> shouldn&apos;t be one prompt injection away.
        </h1>
        <p
          className="text-lg text-text-secondary leading-relaxed max-w-[600px] mx-auto animate-fade-in-up max-md:text-base"
          style={{ animationDelay: "150ms" }}
        >
          Qosm lets you build AI agents you can actually trust.
          Declare exactly which capabilities an agent gets. The compiler guarantees the rest.
        </p>
        <div
          className="flex gap-4 justify-center items-center flex-wrap mt-8 animate-fade-in-up"
          style={{ animationDelay: "250ms" }}
        >
          <WaitlistButton
            className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold rounded-lg no-underline hover:no-underline hover:-translate-y-px transition-all"
            style={{ backgroundColor: accent.DEFAULT, color: accent.onAccent }}
          >
            Join the Waitlist
          </WaitlistButton>
        </div>
      </div>

      {/* Code block + Capabilities side panel */}
      <div
        className="relative z-10 w-full max-w-[1000px] mx-auto animate-fade-in-up"
        style={{ animationDelay: "450ms" }}
      >
        {/* Prompt bubble */}
        <div className="flex items-start gap-3 mb-3 max-md:mb-2">
          <div
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-text-secondary leading-snug border border-border"
            style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
          >
            <span className="text-text-dim shrink-0">&gt;</span>
            <span>
              <span className="italic">
                &ldquo;Read customer revenue data, compute averages per customer, and post a summary to #analytics&rdquo;
              </span>
            </span>
          </div>
        </div>

        <div className="flex gap-4 items-stretch max-md:flex-col max-md:gap-3">
          {/* Code panel */}
          <div
            className="flex-1 min-w-0 rounded-xl overflow-hidden border border-border"
            style={{ boxShadow: `0 0 80px ${accent.shadow}` }}
          >
            <div className="bg-bg-elevated px-4 py-2.5 flex justify-between items-center border-b border-border">
              <span className="font-mono text-xs text-text-muted">analytics.qosm</span>
              <span className="text-[0.6rem] font-semibold uppercase tracking-widest text-text-muted">
                Qosm
              </span>
            </div>

            {/* Host prelude — provided by host, not written by the agent */}
            <div className="bg-bg-surface/60 relative">
              <div className="absolute top-2 right-3 flex items-center gap-1.5">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-dim)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span className="text-[9px] font-semibold uppercase tracking-widest text-text-dim">
                  Provided by host
                </span>
              </div>
              <QosmCode
                code={preludeCode}
                className="m-0 px-5 pt-4 pb-3 text-[13px] leading-relaxed overflow-x-auto opacity-50 max-md:text-sm max-md:px-4"
              />
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 px-5 bg-bg-elevated">
              <span className="flex-1 h-px bg-border" />
              <span className="text-[9px] font-semibold uppercase tracking-widest text-text-dim whitespace-nowrap">
                Agent code
              </span>
              <span className="flex-1 h-px bg-border" />
            </div>

            {/* Animated agent code — cycles through attack/defense scenarios */}
            <CodeAnimation />
          </div>

          {/* Capabilities / attenuations panel */}
          <div
            className="w-[280px] shrink-0 rounded-xl border border-border bg-bg-elevated flex flex-col max-md:w-full animate-fade-in-up"
            style={{ animationDelay: "600ms" }}
          >
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent.DEFAULT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span className="text-xs font-semibold uppercase tracking-widest text-text-muted">
                Host Capabilities
              </span>
            </div>
            <div className="flex-1 flex flex-col gap-0 divide-y divide-bg-surface">
              {capabilities.map((cap, i) => (
                <div
                  key={cap.extern}
                  className="px-4 py-3 animate-fade-in-up"
                  style={{ animationDelay: `${750 + i * 120}ms` }}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: cap.color }}
                    />
                    <span className="font-mono text-xs font-semibold" style={{ color: cap.color }}>
                      {cap.label}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 pl-3.5">
                    <div className="font-mono text-[11px] text-text-secondary">
                      {cap.extern}
                    </div>
                    {cap.attenuations.map((att) => (
                      <div key={att.key} className="flex items-baseline gap-1.5 font-mono text-[11px]">
                        <span className="text-text-dim">{att.key}:</span>
                        <span className="text-text-muted">{att.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-border">
              <p className="text-[10px] text-text-dim leading-relaxed">
                Credentials stay in the host. The agent calls functions. It never sees API keys.
              </p>
            </div>
          </div>
        </div>

        {/* Generation note */}
        <div className="mt-8 text-center max-w-[640px] mx-auto max-md:mt-6">
          <p className="text-base text-text-secondary leading-relaxed max-md:text-sm">
            Qosm is a language and execution environment, not an IDE.
            Generate it directly in the Workspace, or from Claude Code, Codex, Cursor, or any tool you already use.
          </p>
          <p className="mt-3 text-lg font-bold text-text-secondary leading-relaxed max-md:text-base">
            It takes <span style={{ color: "#c4b5fd" }}>less than 1k tokens</span> to teach your favorite model how to program in Qosm.
          </p>
        </div>
      </div>
    </section>
  );
}
